import React, { useEffect, useMemo, useState } from 'react';
import { Download, FileText, Printer, Send, X } from 'lucide-react';
import { Box, Button, CircularProgress, Dialog, FormControl, IconButton, MenuItem, Select, type SelectChangeEvent, Typography } from '@mui/material';
import { format } from 'date-fns';
import { getClientInvoice, resendInvoiceEmail, updateInvoiceStatus, type Invoice } from '@/lib/api/invoices';
import {
  downloadInvoicePdf,
  getInvoiceClinicLogoUrl,
  getInvoicePdfFallbackFromProfile,
  invoiceApiToPdfPayload,
  getInvoiceShowLogoOnInvoice,
  printInvoicePdf,
  withInvoiceClinicLogoDataUrl,
} from '@/utils/invoicePdfExport';
import { useAuth } from '@/hooks/useAuth';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CheckIcon from '@mui/icons-material/Check';
import InvoiceStatusConfirmDialog from '@/components/dashboard/InvoiceStatusConfirmDialog';
import InvoiceLogoHeader from '@/components/dashboard/InvoiceLogoHeader';
import { formatInvoiceCurrencyAmount } from '@/utils/invoiceCurrency';
import { ClientNameLink } from '@/components/clients/ClientNameLink';
import InlineFeedback from '@/components/shared/InlineFeedback';

interface InvoiceDetailsDialogProps {
  open: boolean;
  onClose: () => void;
  invoice: Invoice | null;
  onInvoiceUpdated?: () => void;
}
type StatusSelectValue = 'due' | 'overdue' | 'paid' | 'discarded';
const LIST_MARK_PAID_PAYMENT_METHOD = 'upi';

const getApiErrorMessage = (error: unknown): string => {
  const maybeObj = error as {
    response?: { data?: { error?: string; message?: string } };
    message?: string;
  };
  return maybeObj?.response?.data?.error || maybeObj?.response?.data?.message || maybeObj?.message || 'Request failed';
};

const currencyLabel = (currency: string | undefined, amount: number): string => {
  return formatInvoiceCurrencyAmount(Math.max(0, amount), currency);
};

const formatRateLabel = (value: number): string => {
  if (Number.isInteger(value)) return String(value);
  return value.toFixed(2).replace(/\.?0+$/, '');
};

function normalizeStatusForSelect(status?: string): StatusSelectValue {
  const s = (status || '').toLowerCase();
  if (s === 'paid') return 'paid';
  if (s === 'discarded' || s === 'archived') return 'discarded';
  if (s === 'overdue') return 'overdue';
  return 'due';
}

function mapUiStatusToApiStatus(value: StatusSelectValue): string {
  if (value === 'due') return 'sent';
  return value;
}

function statusLabel(value: StatusSelectValue): string {
  if (value === 'paid') return 'Paid';
  if (value === 'overdue') return 'Overdue';
  if (value === 'discarded') return 'Discarded';
  return 'Due';
}

function statusColor(value: StatusSelectValue): string {
  if (value === 'paid') return '#28c76f';
  if (value === 'overdue') return '#ea5455';
  if (value === 'discarded') return '#64748b';
  return '#ea5455';
}

const invoiceEmailTextSx = {
  whiteSpace: 'normal',
  wordBreak: 'break-word',
  overflowWrap: 'anywhere',
};

const InvoiceDetailsDialog: React.FC<InvoiceDetailsDialogProps> = ({ open, onClose, invoice, onInvoiceUpdated }) => {
  const { user } = useAuth();
  const [invoiceData, setInvoiceData] = useState<Invoice | null>(invoice);
  const [isLoadingInvoice, setIsLoadingInvoice] = useState(false);
  const [isResendingEmail, setIsResendingEmail] = useState(false);
  const [isInvoicePdfBusy, setIsInvoicePdfBusy] = useState(false);
  const [isUpdatingInvoiceStatus, setIsUpdatingInvoiceStatus] = useState(false);
  const [pendingStatusChange, setPendingStatusChange] = useState<StatusSelectValue | null>(null);
  const [invoiceFeedback, setInvoiceFeedback] = useState<{
    variant: 'error' | 'warning' | 'success' | 'info';
    title?: string;
    message: string;
  } | null>(null);

  useEffect(() => {
    if (!open || !invoice?.id) return;
    setInvoiceData(invoice);
    setInvoiceFeedback(null);
    setIsLoadingInvoice(true);
    getClientInvoice(invoice.id)
      .then((fullInvoice) => setInvoiceData(fullInvoice))
      .catch((error) => {
        console.error('Failed to fetch invoice details:', error);
      })
      .finally(() => setIsLoadingInvoice(false));
  }, [open, invoice]);

  const invoiceRef = invoiceData ?? invoice;
  const clinicLogoUrl = getInvoiceClinicLogoUrl(invoiceRef);
  const showLogoOnInvoice = getInvoiceShowLogoOnInvoice(invoiceRef);
  const fromName = invoiceRef?.nutritionistSnapshot?.name || invoiceRef?.nutritionistSnapshot?.fullName || user?.fullName || 'NutriCare Practice';
  const fromEmail = invoiceRef?.nutritionistSnapshot?.email || user?.email || '';
  const selectedClientName =
    invoiceRef?.clientSnapshot?.fullName?.trim() ||
    invoiceRef?.clientSnapshot?.name?.trim() ||
    `${invoiceRef?.clientSnapshot?.firstName ?? ''} ${invoiceRef?.clientSnapshot?.lastName ?? ''}`.trim() ||
    '—';
  const selectedClientEmail = invoiceRef?.clientSnapshot?.email?.trim() || '';
  const invoiceNumber = invoiceRef?.invoiceNo || invoiceRef?.id || '—';
  const issueDate = invoiceRef?.issueDate ? format(new Date(invoiceRef.issueDate), 'd MMM yyyy') : '—';
  const dueDate = invoiceRef?.dueDate ? format(new Date(invoiceRef.dueDate), 'd MMM yyyy') : '—';
  const subscriptionPeriodLabel =
    invoiceRef?.subscriptionStartDate && invoiceRef?.subscriptionEndDate
      ? `${format(new Date(invoiceRef.subscriptionStartDate), 'd MMM yyyy')} – ${format(new Date(invoiceRef.subscriptionEndDate), 'd MMM yyyy')}`
      : null;
  const paidAtDate = invoiceRef?.paidAt ? format(new Date(invoiceRef.paidAt), 'd MMM yyyy') : '—';

  const normalizedLineItems = useMemo(() => {
    return (invoiceRef?.lineItems || []).map((li, idx) => {
      const quantityValue = Number(li.qty) || 0;
      const unitPriceValue = Number(li.unitPrice) || 0;
      return {
        id: idx + 1,
        service: li.description || '-',
        quantityValue,
        unitPriceValue,
        lineTotal: Number(li.total) || quantityValue * unitPriceValue,
      };
    });
  }, [invoiceRef]);

  const subtotalAmount = Number(invoiceRef?.subtotal) || normalizedLineItems.reduce((s, i) => s + i.lineTotal, 0);
  const taxRateValue = Number(invoiceRef?.taxPct) || 0;
  const discountAmount = Number(invoiceRef?.discount) || 0;
  const taxAmount = Number(invoiceRef?.taxAmount) || subtotalAmount * (taxRateValue / 100);
  const totalAmount = Number(invoiceRef?.totalDue) || Math.max(0, subtotalAmount + taxAmount - discountAmount);
  const invoiceNotes = (invoiceRef?.notes || '').trim();

  const statusValue = normalizeStatusForSelect(invoiceRef?.status);
  const isPaid = statusValue === 'paid';
  const paymentDateLabel = isPaid ? 'Paid Date' : 'Due Date';
  const paymentDateValue = isPaid ? paidAtDate : dueDate;

  const handleResendEmail = async () => {
    if (!invoiceRef?.id) return;
    setIsResendingEmail(true);
    try {
      await resendInvoiceEmail(invoiceRef.id);
      setInvoiceFeedback({
        variant: 'success',
        title: 'Invoice email sent',
        message: 'The invoice email was sent successfully.',
      });
    } catch (error) {
      console.error('Failed to resend invoice email:', error);
      setInvoiceFeedback({
        variant: 'error',
        title: 'Failed to send invoice email',
        message: getApiErrorMessage(error),
      });
    } finally {
      setIsResendingEmail(false);
    }
  };

  const handleDownloadInvoicePdf = async () => {
    if (!invoiceRef || isInvoicePdfBusy) return;
    setIsInvoicePdfBusy(true);
    try {
      const fallbackFrom = await getInvoicePdfFallbackFromProfile({
        name: user?.fullName,
        email: user?.email,
        clinicLogoUrl,
        showLogoOnInvoice,
      });
      const payload = await withInvoiceClinicLogoDataUrl(
        invoiceRef.id,
        invoiceApiToPdfPayload(invoiceRef, fallbackFrom)
      );
      await downloadInvoicePdf(payload);
      setInvoiceFeedback({
        variant: 'success',
        title: 'Invoice PDF downloaded',
        message: 'The invoice PDF has been downloaded.',
      });
    } catch (error) {
      console.error('Invoice PDF export failed', error);
      setInvoiceFeedback({
        variant: 'error',
        title: 'Could not generate PDF',
        message: getApiErrorMessage(error),
      });
    } finally {
      setIsInvoicePdfBusy(false);
    }
  };

  const handlePrintInvoicePdf = async () => {
    if (!invoiceRef || isInvoicePdfBusy) return;
    setIsInvoicePdfBusy(true);
    try {
      const fallbackFrom = await getInvoicePdfFallbackFromProfile({
        name: user?.fullName,
        email: user?.email,
        clinicLogoUrl,
        showLogoOnInvoice,
      });
      const payload = await withInvoiceClinicLogoDataUrl(
        invoiceRef.id,
        invoiceApiToPdfPayload(invoiceRef, fallbackFrom)
      );
      await printInvoicePdf(payload);
    } catch (error) {
      console.error('Invoice print failed', error);
      setInvoiceFeedback({
        variant: 'error',
        title: 'Could not open print dialog',
        message: getApiErrorMessage(error),
      });
    } finally {
      setIsInvoicePdfBusy(false);
    }
  };

  const handleInlineStatusChange = async (event: SelectChangeEvent<StatusSelectValue>) => {
    const id = invoiceRef?.id;
    if (!id) return;
    const next = event.target.value as StatusSelectValue;
    const current = normalizeStatusForSelect(invoiceRef?.status);
    if (next === current) return;

    if (next === 'paid' || next === 'discarded') {
      setPendingStatusChange(next);
      return;
    }

    setIsUpdatingInvoiceStatus(true);
    try {
      const updated = await updateInvoiceStatus(id, { status: mapUiStatusToApiStatus(next) });
      setInvoiceData(updated);
      setInvoiceFeedback({
        variant: 'success',
        title: 'Invoice status updated',
        message: `Status changed to ${statusLabel(next)}.`,
      });
      onInvoiceUpdated?.();
    } catch (error) {
      console.error('Failed to update invoice status', error);
      setInvoiceFeedback({
        variant: 'error',
        title: 'Could not update invoice status',
        message: getApiErrorMessage(error),
      });
    } finally {
      setIsUpdatingInvoiceStatus(false);
    }
  };

  const handleConfirmPendingStatusChange = async () => {
    const id = invoiceRef?.id;
    if (!id || !pendingStatusChange) return;
    const next = pendingStatusChange;
    setIsUpdatingInvoiceStatus(true);
    try {
      const updated = await updateInvoiceStatus(
        id,
        next === 'paid'
          ? {
              status: 'paid',
              paidAt: new Date().toISOString(),
              paymentMethod: LIST_MARK_PAID_PAYMENT_METHOD,
            }
          : { status: mapUiStatusToApiStatus(next) }
      );
      setInvoiceData(updated);
      setPendingStatusChange(null);
      setInvoiceFeedback({
        variant: 'success',
        title: 'Invoice status updated',
        message: `Status changed to ${statusLabel(next)}.`,
      });
      onInvoiceUpdated?.();
    } catch (error) {
      console.error('Failed to update invoice status', error);
      setInvoiceFeedback({
        variant: 'error',
        title: 'Could not update invoice status',
        message: getApiErrorMessage(error),
      });
    } finally {
      setIsUpdatingInvoiceStatus(false);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth={false}
        PaperProps={{
          sx: {
            width: '704px',
            maxWidth: 'calc(100vw - 32px)',
            borderRadius: '16px',
            overflow: 'hidden',
            m: 2,
          },
        }}
      >
        <Box sx={{ bgcolor: '#fff' }}>
          <Box sx={{ height: 65, px: 2.5, borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 28, height: 28, borderRadius: '10px', bgcolor: '#ebf9ff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <FileText size={14} color="#20aedc" />
              </Box>
              <Box>
                <Typography sx={{ color: '#111827', fontWeight: 600, fontSize: '13px', lineHeight: '19.5px' }}>Invoice Details</Typography>
                <Typography sx={{ color: '#9ca3af', fontSize: '12px', lineHeight: '16.5px' }}>
                  <ClientNameLink clientId={invoiceRef?.clientId} className="text-[#9ca3af] hover:text-[#6b7280]">
                    {selectedClientName}
                  </ClientNameLink>
                  {' . '}
                  {invoiceNumber}
                </Typography>
              </Box>
            </Box>
            <IconButton size="small" onClick={onClose} sx={{ width: 28, height: 28, borderRadius: '10px', color: '#6b7280' }}>
              <X size={15} />
            </IconButton>
          </Box>

          {invoiceFeedback ? (
            <Box sx={{ px: 2.5, pt: 1.5 }}>
              <InlineFeedback
                variant={invoiceFeedback.variant}
                title={invoiceFeedback.title}
                message={invoiceFeedback.message}
              />
            </Box>
          ) : null}

          {isLoadingInvoice ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Box sx={{ px: 2.5, pt: 2.5, pb: 2, display: 'grid', gap: 2 }}>
                <Box sx={{ border: '1px solid #e5e7eb', borderRadius: '14px', overflow: 'hidden' }}>
                  <InvoiceLogoHeader clinicLogoUrl={clinicLogoUrl} />
                  <Box sx={{ height: 80, px: 3, pt: 2.5, display: 'flex', justifyContent: 'space-between', backgroundImage: 'linear-gradient(171deg, #20aedc 0%, #1a8fb8 60%, #0e6a8a 100%)' }}>
                    <Box>
                      <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '12px', letterSpacing: '1px', textTransform: 'uppercase' }}>Invoice</Typography>
                      <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '24px', lineHeight: '32px' }}>{invoiceNumber}</Typography>
                    </Box>
                   
                  </Box>

                  <Box sx={{ p: 3, display: 'grid', gap: 2.5 }}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0,1fr))', gap: 3 }}>
                      <Box>
                        <Typography sx={{ color: '#99a1af', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '1px' }}>From</Typography>
                        <Typography sx={{ color: '#1e2939', fontWeight: 600, fontSize: '14px', mt: 0.5 }}>{fromName}</Typography>
                        <Typography sx={{ color: '#6a7282', fontSize: '12px', ...invoiceEmailTextSx }}>{fromEmail}</Typography>
                      </Box>
                      <Box>
                        <Typography sx={{ color: '#99a1af', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '1px' }}>Bill To</Typography>
                        <Typography sx={{ color: '#1e2939', fontWeight: 600, fontSize: '14px', mt: 0.5 }}>
                          <ClientNameLink clientId={invoiceRef?.clientId} className="text-[#1e2939] hover:text-[#1e2939]">
                            {selectedClientName}
                          </ClientNameLink>
                        </Typography>
                        <Typography sx={{ color: '#6a7282', fontSize: '12px', ...invoiceEmailTextSx }}>{selectedClientEmail}</Typography>
                      </Box>
                      <Box>
                        <Typography sx={{ color: '#99a1af', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '1px' }}>Issue Date</Typography>
                        <Typography sx={{ color: '#364153', fontSize: '14px', mt: 0.8 }}>{issueDate}</Typography>
                      </Box>
                      <Box>
                        <Typography sx={{ color: '#99a1af', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '1px' }}>{paymentDateLabel}</Typography>
                        <Typography sx={{ color: '#364153', fontSize: '14px', mt: 0.8 }}>{paymentDateValue}</Typography>
                      </Box>
                    </Box>

                    {subscriptionPeriodLabel ? (
                      <Box>
                        <Typography sx={{ color: '#99a1af', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '1px' }}>Subscription Period</Typography>
                        <Typography sx={{ color: '#364153', fontSize: '14px', mt: 0.8 }}>{subscriptionPeriodLabel}</Typography>
                      </Box>
                    ) : null}

                    <Box sx={{ borderTop: '1px solid #f3f4f6' }} />

                    <Box>
                      <Box sx={{ display: 'grid', gridTemplateColumns: '1.5fr 0.5fr 0.8fr 0.8fr', borderBottom: '1px solid #f3f4f6', pb: 1 }}>
                        <Typography sx={{ color: '#99a1af', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '1px' }}>Description</Typography>
                        <Typography sx={{ color: '#99a1af', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '1px', textAlign: 'center' }}>Qty</Typography>
                        <Typography sx={{ color: '#99a1af', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '1px', textAlign: 'right' }}>Unit Price</Typography>
                        <Typography sx={{ color: '#99a1af', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '1px', textAlign: 'right' }}>Total</Typography>
                      </Box>
                      {normalizedLineItems.map((lineItem) => (
                        <Box key={lineItem.id} sx={{ display: 'grid', gridTemplateColumns: '1.5fr 0.5fr 0.8fr 0.8fr', borderBottom: '1px solid #f9fafb', py: 1.6 }}>
                          <Typography sx={{ color: '#364153', fontSize: '14px' }}>{lineItem.service || '-'}</Typography>
                          <Typography sx={{ color: '#4a5565', fontSize: '14px', textAlign: 'center' }}>{lineItem.quantityValue}</Typography>
                          <Typography sx={{ color: '#4a5565', fontSize: '14px', textAlign: 'right' }}>{currencyLabel(invoiceRef?.currency, lineItem.unitPriceValue)}</Typography>
                          <Typography sx={{ color: '#364153', fontWeight: 500, fontSize: '14px', textAlign: 'right' }}>{currencyLabel(invoiceRef?.currency, lineItem.lineTotal)}</Typography>
                        </Box>
                      ))}
                    </Box>

                    <Box sx={{ borderTop: '1px solid #eceff3', pt: 1.5, px: 0.5 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.4 }}>
                        <Typography sx={{ color: '#6a7282', fontSize: '14px' }}>Subtotal</Typography>
                        <Typography sx={{ color: '#6a7282', fontSize: '14px' }}>{currencyLabel(invoiceRef?.currency, subtotalAmount)}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.4 }}>
                        <Typography sx={{ color: '#6a7282', fontSize: '14px' }}>Tax ({formatRateLabel(taxRateValue)}%)</Typography>
                        <Typography sx={{ color: '#6a7282', fontSize: '14px' }}>{currencyLabel(invoiceRef?.currency, taxAmount)}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.8 }}>
                        <Typography sx={{ color: '#6a7282', fontSize: '14px' }}>Discount</Typography>
                        <Typography sx={{ color: '#6a7282', fontSize: '14px' }}>-{currencyLabel(invoiceRef?.currency, discountAmount)}</Typography>
                      </Box>
                      <Box sx={{ borderTop: '1px solid #eceff3', pt: 0.8, display: 'flex', justifyContent: 'space-between' }}>
                        <Typography sx={{ color: '#1e2939', fontWeight: 700, fontSize: '16px' }}>Total</Typography>
                        <Typography sx={{ color: '#20aedc', fontWeight: 700, fontSize: '20px' }}>
                          {currencyLabel(invoiceRef?.currency, totalAmount)}
                        </Typography>
                      </Box>
                    </Box>

                    {invoiceNotes ? (
                      <Box sx={{ bgcolor: 'rgba(239,246,255,0.6)', border: '1px solid #dbeafe', borderRadius: '14px', p: 2 }}>
                        <Typography sx={{ color: '#99a1af', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '1px' }}>Notes</Typography>
                        <Typography sx={{ color: '#4a5565', fontSize: '14px', mt: 0.5 }}>{invoiceNotes}</Typography>
                      </Box>
                    ) : null}
                  </Box>
                </Box>
              </Box>

              <Box sx={{ borderTop: '1px solid #f3f4f6', px: '20px', py: '11px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  <Button variant="outlined" startIcon={isResendingEmail ? <CircularProgress size={14} color="inherit" /> : <Send size={14} />} onClick={handleResendEmail} sx={{ height: 38, borderRadius: '14px', textTransform: 'none', borderColor: '#e5e7eb', color: '#4a5565', fontFamily: '"Inter", sans-serif', fontWeight: 500, fontSize: '14px', letterSpacing: '-0.1504px', px: '12px', '& .MuiButton-startIcon': { mr: '6px' }, '&:hover': { borderColor: '#d1d5db', bgcolor: '#f8fafc' } }}>Send Email</Button>
                  <Button variant="outlined" startIcon={isInvoicePdfBusy ? <CircularProgress size={14} color="inherit" /> : <Download size={14} />} disabled={isInvoicePdfBusy} onClick={() => void handleDownloadInvoicePdf()} sx={{ height: 38, borderRadius: '14px', textTransform: 'none', borderColor: '#e5e7eb', color: '#4a5565', fontFamily: '"Inter", sans-serif', fontWeight: 500, fontSize: '14px', letterSpacing: '-0.1504px', px: '12px', '& .MuiButton-startIcon': { mr: '6px' }, '&:hover': { borderColor: '#d1d5db', bgcolor: '#f8fafc' } }}>Download</Button>
                  <Button variant="outlined" startIcon={isInvoicePdfBusy ? <CircularProgress size={14} color="inherit" /> : <Printer size={14} />} disabled={isInvoicePdfBusy} onClick={() => void handlePrintInvoicePdf()} sx={{ height: 38, borderRadius: '14px', textTransform: 'none', borderColor: '#e5e7eb', color: '#4a5565', fontFamily: '"Inter", sans-serif', fontWeight: 500, fontSize: '14px', letterSpacing: '-0.1504px', px: '12px', '& .MuiButton-startIcon': { mr: '6px' }, '&:hover': { borderColor: '#d1d5db', bgcolor: '#f8fafc' } }}>Print</Button>
                </Box>
                <FormControl size="small">
                  <Select<StatusSelectValue>
                    value={statusValue}
                    onChange={(e) => void handleInlineStatusChange(e)}
                    IconComponent={KeyboardArrowDownIcon}
                    sx={{
                      minWidth: 170,
                      height: 38,
                      borderRadius: '14px',
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e5e7eb' },
                      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#e5e7eb' },
                      '& .MuiSelect-select': { py: '8px', pl: 1.5, pr: 3 },
                    }}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                        {selected === 'paid' ? <CheckIcon sx={{ fontSize: 14, color: '#28c76f' }} /> : null}
                        <Typography sx={{ fontSize: '14px', fontWeight: 600, color: statusColor(selected), letterSpacing: '-0.15px' }}>
                          {isUpdatingInvoiceStatus ? 'Updating...' : statusLabel(selected)}
                        </Typography>
                      </Box>
                    )}
                  >
                    <MenuItem value="due">Due</MenuItem>
                    <MenuItem value="overdue">Overdue</MenuItem>
                    <MenuItem value="paid">Paid</MenuItem>
                    <MenuItem value="discarded">Discarded</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </>
          )}
        </Box>
      </Dialog>
      <InvoiceStatusConfirmDialog
        open={Boolean(pendingStatusChange)}
        targetStatus={pendingStatusChange === 'paid' ? 'paid' : pendingStatusChange === 'discarded' ? 'discarded' : null}
        isSubmitting={isUpdatingInvoiceStatus}
        onCancel={() => setPendingStatusChange(null)}
        onConfirm={() => void handleConfirmPendingStatusChange()}
      />
    </>
  );
};

export default InvoiceDetailsDialog;
